import React, { useRef, useEffect, useState } from "react";
import { NavLink, useNavigate, useLocation } from "react-router-dom";
import Employee from "../../components/timesheet/Employee";
import { useSidebarData } from "../../hooks/sidebarData";
import Dtr from "../../components/timesheet/Dtr";
import Swal from "sweetalert2";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import axios from "../../api/axios";
import axiosaim from "../../api/axiosaim";
import toast from "../../components/sweetalert/Sweetalert";
import { destroySession } from "../../helpers/Session";

const Employees = () => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { setSidebarData } = useSidebarData();

  const timeoutRef = useRef(null);
  const [hasEmployeeTimesheets, setHasEmployeeTimesheets] = useState(false);
  const [accessibleDepartments, setAccessibleDepartments] = useState([]);

  const accesstoken = sessionStorage.getItem("accesstoken");
  const aimtoken = sessionStorage.getItem("aimtoken") || "";
  const uname = sessionStorage.getItem("uname");
  const email = sessionStorage.getItem("email");
  const department = sessionStorage.getItem("department");
  const departmentslug = sessionStorage.getItem("departmentslug");
  const breadcrumbChild =
    sessionStorage.getItem("timesheetChild") || "Default Child";
  const breadcrumbChildSlug = sessionStorage.getItem("timesheetChildSlug");
  const breadcrumbGrandchild =
    sessionStorage.getItem("timesheetGrandchild") || "Grandchild";
  const breadcrumbGrandchildSlug = sessionStorage.getItem(
    "timesheetGrandchildSlug"
  );
  const breadcrumbPayperiod =
    sessionStorage.getItem("timesheetPayperiod") || "Pay Period";
  const breadcrumbPayperiodId = sessionStorage.getItem("timesheetPayperiodId");
  const breadcrumbEmployee =
    sessionStorage.getItem("timesheetEmployee") || "Employee";
  const [timesheetEmployeeUname, setTimesheetEmployeeUname] = useState(
    sessionStorage.getItem("timesheetEmployeeUname")
  );

  const { data: role } = useQuery({
    queryKey: ["userrole"],
  });

  const { data: manageddepartments } = useQuery({
    queryKey: ["managedDepartments", uname],
  });

  const { data: managedadjustments } = useQuery({
    queryKey: ["managedAdjustments", uname],
  });

  const { data: manageddepartmentsAIM = [], isLoading: loadingAIM } = useQuery({
    queryKey: ["managedAIMDepartments", uname],
    queryFn: async () => {
      const res = await axiosaim.get(`getmanageddepartments`, {
        headers: { "X-Aim-Token": aimtoken },
      });
      // AIM API call for department head detection
      return res.data;
    },
    enabled: !!uname && !!aimtoken,
    retry: 1
  });

  const isDepartmentHead =
    Array.isArray(manageddepartmentsAIM) && manageddepartmentsAIM.length > 0;

  const isHeadOfThisDepartment = Array.isArray(manageddepartmentsAIM) &&
    manageddepartmentsAIM.some(dept => dept.department_code === departmentslug);

  // Department head detection logic

  useEffect(() => {
    const combineDepartments = [...(manageddepartments || []), ...(managedadjustments || [])];
    const finalDepartments = combineDepartments.filter((value, index, self) => (
        index === self.findIndex((d) => (
            d.department_code === value.department_code && d.department === value.department
        ))
    ));

    if (JSON.stringify(finalDepartments) !== JSON.stringify(accessibleDepartments)) {
        setAccessibleDepartments(finalDepartments);
    }
  }, [manageddepartments, managedadjustments, accessibleDepartments]);

  // Clear employee data if browser back navigates to employee list URL
  const location = useLocation();
  useEffect(() => {
    // Count URL segments after /dtr/timesheets/
    const pathSegments = location.pathname.split('/').filter(Boolean);
    const timesheetIndex = pathSegments.indexOf('timesheets');
    const segmentsAfterTimesheets = pathSegments.length - timesheetIndex - 1;
    const lastSegment = pathSegments[pathSegments.length - 1];

    // Determine if we're on employee list or individual employee view
    // Admin/Management: dept/year/payperiod = 3 segments (list), dept/year/payperiod/uname = 4 segments (individual)
    // Manager: dept/payperiod = 2 segments (list), dept/payperiod/uname = 3 segments (individual)
    // If last segment is a number (payperiod ID), we're on employee list
    // If last segment is alphanumeric (username), we're on individual view
    const isPayperiodId = /^\d+$/.test(lastSegment);

    // Clear employee data only if we're on employee list URL (last segment is payperiod ID)
    if (isPayperiodId || segmentsAfterTimesheets === 1) {
      const storedEmployeeUname = sessionStorage.getItem('timesheetEmployeeUname');
      if (storedEmployeeUname) {
        sessionStorage.removeItem('timesheetEmployee');
        sessionStorage.removeItem('timesheetEmployeeUname');
      }
      setTimesheetEmployeeUname(null);
    }
  }, [location.pathname]);

  const isApproverOfThisDepartment = accessibleDepartments.some(dept => dept.department_code === breadcrumbChildSlug);

  // Enhanced logic: Multiple ways to determine if user can view unsubmitted timesheets
  const canViewUnsubmittedTimesheets = isHeadOfThisDepartment || isApproverOfThisDepartment ||
    // Fallback: if user has manager role and is viewing their own department
    (role === 'manager' && departmentslug === breadcrumbChildSlug);


  const shouldShowSubmitButton = () => {
    // Only department heads can submit department timesheets
    // This should ONLY depend on AIM API department head detection, not approver permissions
    // Approver permissions (manageddepartments/managedadjustments) are for individual timesheet approval only
    return isHeadOfThisDepartment && hasEmployeeTimesheets;
  };

  const breadcrumbs = [
    { name: "DTR", path: "/dtr/my-dtr" },
    { name: "Timesheets", path: "/dtr/timesheets" },
    { name: breadcrumbChild, path: `/dtr/timesheets/${breadcrumbChildSlug}` },
    ...((role === "admin" || role === "management") || isDepartmentHead
      ? [
          {
            name: breadcrumbGrandchild,
            path: `/dtr/timesheets/${breadcrumbChildSlug}/${breadcrumbGrandchildSlug}`,
          },
          // If viewing individual employee, show employee name, otherwise show pay period
          timesheetEmployeeUname
            ? { name: breadcrumbEmployee, path: "" }
            : { name: breadcrumbPayperiod, path: "" },
        ]
      : [
          {
            name: breadcrumbPayperiod,
            path: `/dtr/timesheets/${breadcrumbChildSlug}/${breadcrumbPayperiodId}`,
          },
          // If viewing individual employee, show employee name
          ...(timesheetEmployeeUname ? [{ name: breadcrumbEmployee, path: "" }] : []),
        ]),
  ];

  const handleClearSidebarData = () => {
    setSidebarData("state", false);
    setSidebarData("key", "");
    setSidebarData("label", "");
    setSidebarData("data", []);
  };

  const { data: status, isLoadingCutoffApproval } = useQuery({
    queryKey: ["departmentCutoff"],
    queryFn: async () => {
      const departmentToCheck = breadcrumbChildSlug;
      const res = await axios.get(`payperiods/departments?department=${departmentToCheck}&payperiod=${breadcrumbPayperiodId}`, {
        headers: { Authorization: `Bearer ${accesstoken}` },
      });
      return res.data.response;
    },
    enabled: !!breadcrumbChildSlug && !!breadcrumbPayperiodId && isHeadOfThisDepartment,
    retry: 1,
  });

  const submitDepartmentDtrMutation = useMutation({
    mutationFn: async (departmentData) => {
      const res = await axios.post("payperiods/departments", departmentData, {
        headers: { Authorization: `Bearer ${accesstoken}` },
      });
      return res.data.response;
    },
    onSuccess: (data) => {
      toast.fire({ icon: "success", title: data });
      queryClient.invalidateQueries(["departmentCutoff"]);
    },
    onError: (error) => {
      const errorMessage =
        error.response?.data.response ||
        "Something went wrong while sending department timesheets for approval!";
      toast({ icon: "error", title: errorMessage });
      if (error.response?.data.status === "inactive") {
        destroySession();
        clearTimeout(timeoutRef.current);
        timeoutRef.current = setTimeout(() => {
          navigate("/error");
        }, 1000);
      }
    },
  });

  const handleSubmitDepartmentDTR = () => {
    Swal.fire({
      text: `Are you sure you want to submit your department timesheets for ${breadcrumbPayperiod}? Please note that only approved timesheets will be sent to the approving officer.`,
      icon: "warning",
      allowOutsideClick: false,
      showCancelButton: true,
      confirmButtonColor: "#3085d6",
      cancelButtonColor: "#d33",
      confirmButtonText: "Yes",
      cancelButtonText: "No",
      showLoaderOnConfirm: true,
      preConfirm: async (result) => {
        if (result) {
          await submitDepartmentDtrMutation.mutate({
            uname,
            email,
            department: department,
            departmentcode: departmentslug,
            payperiod: breadcrumbPayperiod,
            payperiodid: breadcrumbPayperiodId,
          });
        }
      },
    });
  };


  return (
    <div className="employees">
      <div className="page-header">
        <div className="title with-2-cols">
          <div className="left-panel">
            <h1>Timesheets</h1>
            <ul className="breadcrumbs">
              {breadcrumbs?.map((crumb, index) => (
                <li key={index}>
                  {crumb.path ? (
                    <NavLink to={crumb.path} onClick={handleClearSidebarData}>
                      {crumb.name}
                    </NavLink>
                  ) : (
                    <span>{crumb.name}</span>
                  )}
                </li>
              ))}
            </ul>
          </div>

          {shouldShowSubmitButton() && (
              <div className={`right-panel ${isLoadingCutoffApproval && ' loading'}`}>
                {
                  isLoadingCutoffApproval
                   ? (
                      <h3>Loading...</h3>
                   ) : (
                      status === 0
                        ? (
                            <h3 className="pending">Submitted</h3>
                        ) : (
                            status === 1
                              ? (
                                  <h3 className="approved">Submitted</h3>
                              ) : (
                                  status === 2
                                    ? (
                                        <h3 className="disapproved">Disapproved</h3>
                                    ) : (
                                        <button type="button" onClick={handleSubmitDepartmentDTR}>
                                          <span>SUBMIT</span>
                                          <i className="fa fa-send"></i>
                                        </button>
                                    )
                              )
                        )
                      )
                }
              </div>
          )}
        </div>
      </div>

      <div className="content">
        <div className="files">
          {timesheetEmployeeUname
            ? <Dtr />
            : ((role === "admin" || role === "management") || canViewUnsubmittedTimesheets
                ? <Employee
                    isDepartmentHead={canViewUnsubmittedTimesheets}
                    onEmployeeTimesheetsChange={setHasEmployeeTimesheets}
                  />
                : <Dtr />
              )
          }
        </div>
      </div>
    </div>
  );
};

export default Employees;